import React, { useState, useEffect } from 'react';
import { Grid, Typography, Button, MenuItem, Select, FormControl, InputLabel, TextField, useMediaQuery, IconButton, Menu, ListItemIcon } from '@mui/material';
import { useUserContext } from '../../../context/user_context/UserContext';
import { Loading } from '../../../components/loading/Loading';
import AppSidebar from '../../../components/navbar/DesktopAppSidebar';
import InvoicesTable from './InvoicesTable';
import AddInvoiceModal from './modals/AddInvoiceModal';
import { Add, FilterAlt, MoreVert, Edit as IconPencil } from '@mui/icons-material';
import { organizationInvoiceSnapshotListener } from '../../../repository/InvoicesDAO';
import { getAllProducts } from '../../../repository/ProductsDAO';
import { useOrganizationContext } from '../../../context/organization_context/OrganizationContext';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../resources/strings/StringKeys';
import EditBrandingModal from './modals/EditBrandingModal';
import CustomizeInvoicesModal from './modals/CustomizeInvoiceModal';
import { TablePaginationKeys } from '../../../components/pagination/TablePaginationKeys';
import { handleChangePage, handleChangeRowsPerPage } from '../../../components/pagination/PaginationHelpers';
import FilterDialog from '../../../components/filter_dialog/FilterDialog';
import usePermissions from '../../../utils/permissions/UsePermissions';
import { Modules } from '../../../utils/permissions/Modules';
import { PermissionTypes } from '../../../utils/permissions/PermissionTypes';
import { useNavigate } from 'react-router-dom';

function Invoices() {
    const { firebaseUser } = useUserContext();
    const [showAddInvoiceModal, setShowAddInvoiceModal] = useState(false);
    const [showEditBrandingModal, setShowEditBrandingModal] = useState(false);
    const [showCustomizeInvoicesModal, setShowCustomizeInvoicesModal] = useState(false);
    const [invoices, setInvoices] = useState();
    const [products, setProducts] = useState();
    const { getCustomersSortedByLastName, organizationCustomers, organizationStripeInfo } = useOrganizationContext();
    const { t } = useTranslation();
    const isMobile = useMediaQuery('(max-width:600px)');
    const [customerFilter, setCustomerFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [anchorEl, setAnchorEl] = useState(null);
    const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const { hasPermission } = usePermissions();
    const navigate = useNavigate();
    useEffect(() => {
        const savedRowsPerPage = localStorage.getItem(TablePaginationKeys.InvoiceRowsPerPage);
        if (savedRowsPerPage) {
            setRowsPerPage(parseInt(savedRowsPerPage, 10));
        }
    }, []);

    useEffect(() => {
        if (firebaseUser) {
            const unsubscribe = organizationInvoiceSnapshotListener(firebaseUser.organization, setInvoices);
            return () => unsubscribe();
        }
    }, [firebaseUser]);


    const getFilteredInvoices = () => {
        return invoices
            .filter(invoice => {
                const customerMatch = customerFilter === 'all' || invoice.customer_id === customerFilter;
                const statusMatch = statusFilter === 'all' || 
                    (statusFilter === 'refunded' ? invoice.refunded : (!invoice.refunded && invoice.status === statusFilter));
                const searchMatch = searchTerm === '' || 
                    (invoice.processing ? true : // Always show processing invoices when no search term
                    (invoice.number ? invoice.number.toLowerCase().includes(searchTerm.toLowerCase()) : false));
                const invoiceDate = new Date(invoice.created_at.seconds * 1000); 
                if (isNaN(invoiceDate.getTime())) {
                    return false;
                }
                const startDateMatch = startDate ? new Date(startDate) <= invoiceDate : true;
                const endDateMatch = endDate ? new Date(endDate) >= invoiceDate : true;
                const dateMatch = startDateMatch && endDateMatch;
                return customerMatch && statusMatch && searchMatch && dateMatch;
            })
            .sort((a, b) => b.created_at.seconds - a.created_at.seconds);
    };    
    
    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleOpenBottomSheet = () => {
        setIsBottomSheetOpen(true);
    };

    const handleCloseBottomSheet = () => {
        setIsBottomSheetOpen(false);
    };

    return (
        <>
            {invoices && organizationStripeInfo ? (
                <AppSidebar
                    content={
                        <>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12}>
                                <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                                    {!isMobile && (
                                        <>
                                            <Grid item>
                                                <Typography variant="h5">{t(StringKeys.INVOICES)}</Typography>
                                            </Grid>
                                            <Grid item>
                                                <Grid container spacing={2} alignItems="center">
                                                    <Grid item>
                                                        <TextField
                                                            label={t(StringKeys.SEARCH)}
                                                            variant="outlined"
                                                            size="small"
                                                            value={searchTerm}
                                                            onChange={(e) => setSearchTerm(e.target.value)}
                                                            fullWidth
                                                        />
                                                    </Grid>
                                                    <Grid item>
                                                        <FormControl variant="outlined" size="small" style={{ minWidth: 130 }}>
                                                            <InputLabel>{t(StringKeys.CUSTOMER)}</InputLabel>
                                                            <Select
                                                                value={customerFilter}
                                                                onChange={(e) => setCustomerFilter(e.target.value)}
                                                                label={t(StringKeys.CUSTOMER)}
                                                            >
                                                                <MenuItem value="all">{t(StringKeys.ALL)}</MenuItem>
                                                                {getCustomersSortedByLastName().map(customer => (
                                                                    <MenuItem key={customer.id} value={customer.id}>{customer.first_name} {customer.last_name}</MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item>
                                                        <FormControl variant="outlined" size="small" style={{ minWidth: 130 }}>
                                                            <InputLabel>{t(StringKeys.STATUS)}</InputLabel>
                                                            <Select
                                                                value={statusFilter}
                                                                onChange={(e) => setStatusFilter(e.target.value)}
                                                                label={t(StringKeys.STATUS)}
                                                            >
                                                                <MenuItem value="all">{t(StringKeys.ALL)}</MenuItem>
                                                                <MenuItem value="draft">{t(StringKeys.DRAFT)}</MenuItem>
                                                                <MenuItem value="open">{t(StringKeys.OPEN)}</MenuItem>
                                                                <MenuItem value="paid">{t(StringKeys.PAID)}</MenuItem>
                                                                <MenuItem value="void">{t(StringKeys.VOID)}</MenuItem>
                                                                <MenuItem value="refunded">{t(StringKeys.REFUNDED)}</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    {/* <Grid item>
                                                        <TextField
                                                            label={t(StringKeys.START_DATE)}
                                                            variant="outlined"
                                                            size="small"
                                                            type="date"
                                                            InputLabelProps={{
                                                                shrink: true,
                                                            }}
                                                            value={startDate}
                                                            onChange={(e) => setStartDate(e.target.value)}
                                                            fullWidth
                                                        />
                                                    </Grid>
                                                    <Grid item>
                                                        <TextField
                                                            label={t(StringKeys.END_DATE)}
                                                            variant="outlined"
                                                            size="small"
                                                            type="date"
                                                            InputLabelProps={{
                                                                shrink: true,
                                                            }}
                                                            value={endDate}
                                                            onChange={(e) => setEndDate(e.target.value)}
                                                            fullWidth
                                                        />
                                                    </Grid> */}
                                                    {!isMobile && (
                                                        <>
                                                            <Grid item>
                                                                {hasPermission(Modules.INVOICES, PermissionTypes.WRITE) && (
                                                                    <Grid container spacing={2} alignItems="center">
                                                                        {/* <Grid item>
                                                                            <Button
                                                                                variant="contained"
                                                                                onClick={() => setShowCustomizeInvoicesModal(true)}  
                                                                                startIcon={<IconPencil />}
                                                                            >
                                                                                {t(StringKeys.CUSTOMIZE_INVOICES)}
                                                                            </Button>
                                                                        </Grid>                                                 */}
                                                                        <Grid item>
                                                                            <Button
                                                                                variant="contained"
                                                                                onClick={() => navigate('/invoice-creation-form')}
                                                                                startIcon={<Add />}
                                                                            >
                                                                                {t(StringKeys.CREATE_INVOICE)}
                                                                            </Button>
                                                                        </Grid>
                                                                    </Grid>
                                                                )}
                                                            </Grid>
                                                        </>
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </>
                                    )}
                                    {isMobile && (
                                        <>
                                            <Grid item>
                                                <Typography variant="h6">{t(StringKeys.INVOICES)}</Typography>
                                            </Grid>
                                            <Grid item xs={10}>
                                                <TextField
                                                    label={t(StringKeys.SEARCH)}
                                                    variant="outlined"
                                                    size="small"
                                                    fullWidth
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                />
                                            </Grid>
                                            <Grid item xs={2} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                <IconButton onClick={handleMenuClick}>
                                                    <MoreVert />
                                                </IconButton>
                                                <Menu
                                                    anchorEl={anchorEl}
                                                    open={Boolean(anchorEl)}
                                                    onClose={handleMenuClose}
                                                >
                                                    <MenuItem onClick={() => { handleOpenBottomSheet(); handleMenuClose(); }}>
                                                        <ListItemIcon>
                                                            <FilterAlt />
                                                        </ListItemIcon>
                                                        {t(StringKeys.FILTER)}
                                                    </MenuItem>
                                                    {hasPermission(Modules.INVOICES, PermissionTypes.WRITE) && (
                                                        <>
                                                            {/* <MenuItem onClick={() => { setShowCustomizeInvoicesModal(true); handleMenuClose(); }}>
                                                                <ListItemIcon>
                                                                    <IconPencil />
                                                                </ListItemIcon>
                                                                {t(StringKeys.CUSTOMIZE_INVOICES)}
                                                            </MenuItem> */}
                                                            <MenuItem 
                                                                onClick={() => navigate('/invoice-creation-form')}
                                                            >
                                                                <ListItemIcon>
                                                                    <Add />
                                                                </ListItemIcon>
                                                                {t(StringKeys.CREATE_INVOICE)}
                                                            </MenuItem>
                                                        </>
                                                    )}
                                                </Menu>
                                            </Grid>
                                        </>
                                    )}
                                </Grid>
                            </Grid>

                            {/* Invoices Table */}
                            <Grid item xs={12}>
                                <InvoicesTable 
                                    invoices={getFilteredInvoices()} 
                                    customers={organizationCustomers}
                                    page={page}
                                    rowsPerPage={rowsPerPage}
                                    handleChangePage={(event, newPage) => handleChangePage(event, newPage, setPage)}
                                    handleChangeRowsPerPage={(event) => handleChangeRowsPerPage(event, TablePaginationKeys.InvoiceRowsPerPage, setPage, setRowsPerPage)}
                                />
                            </Grid>
                        </Grid>

                        {/* Modals */}
                        {/* <AddInvoiceModal
                            show={showAddInvoiceModal}
                            onHide={() => setShowAddInvoiceModal(false)}
                            products={products}
                            customers={organizationCustomers}
                            accountId={organizationStripeInfo.stripe_account_id}
                        /> */}
                        <EditBrandingModal
                            show={showEditBrandingModal}
                            onHide={() => setShowEditBrandingModal(false)}
                            accountId={organizationStripeInfo.stripe_account_id}
                        />
                        <CustomizeInvoicesModal
                            show={showCustomizeInvoicesModal}
                            onHide={() => setShowCustomizeInvoicesModal(false)}
                            accountId={organizationStripeInfo.stripe_account_id}
                        />
                        <FilterDialog 
                            open={isBottomSheetOpen}
                            onClose={handleCloseBottomSheet}
                            sortOrder={null} 
                            setSortOrder={null}
                            filters={[
                                <Grid item>
                                    <FormControl variant="outlined" fullWidth size="small" style={{ minWidth: 130 }}>
                                        <InputLabel>{t(StringKeys.CUSTOMER)}</InputLabel>
                                        <Select
                                            value={customerFilter}
                                            onChange={(e) => setCustomerFilter(e.target.value)}
                                            label={t(StringKeys.CUSTOMER)}
                                        >
                                            <MenuItem value="all">{t(StringKeys.ALL)}</MenuItem>
                                                {getCustomersSortedByLastName().map(customer => (
                                                    <MenuItem key={customer.id} value={customer.id}>{customer.first_name} {customer.last_name}</MenuItem>
                                                ))}
                                        </Select>
                                    </FormControl>
                                </Grid>,
                                <Grid item>
                                    <FormControl variant="outlined" fullWidth size="small" style={{ minWidth: 130 }}>
                                        <InputLabel>{t(StringKeys.STATUS)}</InputLabel>
                                        <Select
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                            label={t(StringKeys.STATUS)}
                                        >
                                            <MenuItem value="all">{t(StringKeys.ALL)}</MenuItem>
                                            <MenuItem value="draft">{t(StringKeys.DRAFT)}</MenuItem>
                                            <MenuItem value="open">{t(StringKeys.OPEN)}</MenuItem>
                                            <MenuItem value="paid">{t(StringKeys.PAID)}</MenuItem>
                                            <MenuItem value="void">{t(StringKeys.VOID)}</MenuItem>
                                            <MenuItem value="refunded">{t(StringKeys.REFUNDED)}</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            ]}
                        />
                        </>

                        
                    }
                />
            ) : (
                <AppSidebar content={<Loading />} />
            )}
        </>
    );
}

export default Invoices;
